import { repApi, qs } from './repApi';
import { gridWindowQuery } from './gridWindow';
import type { GridWindow, GridWindowArgs } from './gridWindow';
import type { GridSettings } from './gridSettings';
import type { TrainingAttendee, TrainingClass, TrainingStatus, TrainingStatusRow } from '@/types';

export const trainingApi = repApi.injectEndpoints({
  endpoints: (builder) => ({
    /** One page of the Training Classes table. */
    trainingClassesWindow: builder.query<GridWindow<TrainingClass>, GridWindowArgs>({
      query: (args) => `/training/classes/grid${gridWindowQuery(args)}`,
      providesTags: ['TrainingClass'],
    }),
    /** The Training Classes filter sections; untyped until `readFilterSections` checks the body. */
    trainingClassesFilters: builder.query<unknown, void>({
      query: () => '/training/classes/grid/filter',
      providesTags: ['TrainingClass'],
    }),
    /** The user's saved Training Classes table settings (columns, filters, sort). */
    trainingClassesSettings: builder.query<GridSettings, void>({
      query: () => '/training/classes/grid/columns',
      // Read once per visit: the page owns the settings after the first load.
      keepUnusedDataFor: 0,
    }),
    saveTrainingClassesSettings: builder.mutation<GridSettings, GridSettings>({
      query: (body) => ({ url: '/training/classes/grid/columns', method: 'POST', body }),
    }),
    /** Excel export of the whole filtered, sorted table (the grid's "Print table"). */
    printTrainingClasses: builder.query<{ fileName: string; base64: string }, GridWindowArgs>({
      query: (args) => `/training/classes/grid${gridWindowQuery(args)}&print=1`,
      keepUnusedDataFor: 0,
    }),
    /** One page of a class's Training Attendees table. */
    trainingAttendeesWindow: builder.query<
      GridWindow<TrainingAttendee>,
      GridWindowArgs & { classId: number }
    >({
      query: (args) => `/training/attendees/grid${gridWindowQuery(args)}&classId=${args.classId}`,
      providesTags: (_r, _e, { classId }) => [{ type: 'TrainingAttendees', id: classId }],
    }),
    /**
     * The class's attendee filter sections; untyped until `readFilterSections` checks the body.
     * The API lists values only within `classId` (the old grid left it off and got none).
     */
    trainingAttendeesFilters: builder.query<unknown, number>({
      query: (classId) => `/training/attendees/grid/filter?classId=${classId}`,
      providesTags: (_r, _e, classId) => [{ type: 'TrainingAttendees', id: classId }],
    }),
    /** The user's saved Training Attendees table settings, shared by every class. */
    trainingAttendeesSettings: builder.query<GridSettings, void>({
      query: () => '/training/attendees/grid/columns',
      keepUnusedDataFor: 0,
    }),
    saveTrainingAttendeesSettings: builder.mutation<GridSettings, GridSettings>({
      query: (body) => ({ url: '/training/attendees/grid/columns', method: 'POST', body }),
    }),
    /** Excel export of the class's whole filtered, sorted attendee table. */
    printTrainingAttendees: builder.query<
      { fileName: string; base64: string },
      GridWindowArgs & { classId: number }
    >({
      query: (args) =>
        `/training/attendees/grid${gridWindowQuery(args)}&classId=${args.classId}&print=1`,
      keepUnusedDataFor: 0,
    }),
    getTrainingClass: builder.query<TrainingClass, number>({
      query: (id) => `/training/classes/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'TrainingClass', id }],
    }),
    createTrainingClass: builder.mutation<TrainingClass, Partial<TrainingClass>>({
      query: (body) => ({ url: '/training/classes', method: 'POST', body }),
      invalidatesTags: ['TrainingClass', 'TrainingStatus'],
    }),
    updateTrainingClass: builder.mutation<
      TrainingClass,
      { id: number; body: Partial<TrainingClass> }
    >({
      query: ({ id, body }) => ({ url: `/training/classes/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => ['TrainingClass', { type: 'TrainingClass', id }],
    }),
    deleteTrainingClass: builder.mutation<{ pvDataID: number }, number>({
      query: (id) => ({ url: `/training/classes/${id}`, method: 'DELETE' }),
      invalidatesTags: ['TrainingClass'],
    }),
    classAttendees: builder.query<TrainingAttendee[], number>({
      query: (classId) => `/training/classes/${classId}/attendees`,
      providesTags: (_r, _e, classId) => [{ type: 'TrainingAttendees', id: classId }],
    }),
    addAttendee: builder.mutation<
      TrainingAttendee,
      { classId: number; body: Partial<TrainingAttendee> }
    >({
      query: ({ classId, body }) => ({
        url: `/training/classes/${classId}/attendees`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { classId }) => [
        { type: 'TrainingAttendees', id: classId },
        { type: 'TrainingClass', id: classId },
      ],
    }),
    deleteAttendee: builder.mutation<{ pvDataID: number }, { classId: number; attendeeId: number }>(
      {
        query: ({ classId, attendeeId }) => ({
          url: `/training/classes/${classId}/attendees/${attendeeId}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_r, _e, { classId }) => [
          { type: 'TrainingAttendees', id: classId },
          { type: 'TrainingClass', id: classId },
        ],
      },
    ),
    trainingStatus: builder.query<TrainingStatus, { year?: string | number } | void>({
      query: (arg) => `/training/status${qs({ year: arg ? arg.year : undefined })}`,
      providesTags: ['TrainingStatus'],
    }),
    trainingStatusYears: builder.query<(string | number)[], void>({
      query: () => '/training/status/years',
      providesTags: ['TrainingStatus'],
    }),
    updateTrainingStatus: builder.mutation<TrainingStatusRow[], { trainings: TrainingStatusRow[] }>(
      {
        query: (body) => ({ url: '/training/status', method: 'PUT', body }),
        invalidatesTags: ['TrainingStatus'],
      },
    ),
    archiveTrainingStatus: builder.mutation<{ year: number | string; archived: number }, void>({
      query: () => ({ url: '/training/status/archive', method: 'POST' }),
      invalidatesTags: ['TrainingStatus'],
    }),
    completeTrainingStatus: builder.mutation<{ completed: number }, void>({
      query: () => ({ url: '/training/status/complete', method: 'POST' }),
      invalidatesTags: ['TrainingStatus'],
    }),
    // Public (no token needed; the base query still sends one if present)
    latestPublicClass: builder.query<TrainingClass | null, { county?: string } | void>({
      query: (arg) => `/public/training/latest${qs({ county: arg ? arg.county : undefined })}`,
    }),
    publicRegister: builder.mutation<
      TrainingAttendee,
      { classId: number; pvName: string; pvCounty: string; pvEmail?: string }
    >({
      query: (body) => ({ url: '/public/training/register', method: 'POST', body }),
    }),
  }),
});

export const {
  useTrainingClassesWindowQuery,
  useTrainingClassesFiltersQuery,
  useTrainingClassesSettingsQuery,
  useSaveTrainingClassesSettingsMutation,
  useLazyPrintTrainingClassesQuery,
  useTrainingAttendeesWindowQuery,
  useTrainingAttendeesFiltersQuery,
  useTrainingAttendeesSettingsQuery,
  useSaveTrainingAttendeesSettingsMutation,
  useLazyPrintTrainingAttendeesQuery,
  useGetTrainingClassQuery,
  useCreateTrainingClassMutation,
  useUpdateTrainingClassMutation,
  useDeleteTrainingClassMutation,
  useClassAttendeesQuery,
  useAddAttendeeMutation,
  useDeleteAttendeeMutation,
  useTrainingStatusQuery,
  useTrainingStatusYearsQuery,
  useUpdateTrainingStatusMutation,
  useArchiveTrainingStatusMutation,
  useCompleteTrainingStatusMutation,
  useLatestPublicClassQuery,
  usePublicRegisterMutation,
} = trainingApi;
